import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconPlus, IconTrash, IconBellRinging } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';

const DEFAULT_TEMPLATE =
    'Olá {cliente}, o título "{descricao}" (parcela {parcela}) no valor de R$ {saldo} vence em {vencimento}.';

const CollectionRules = () => {
    const [appId, setAppId] = useState(null);
    const [rules, setRules] = useState([]);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState({ name: '', days_offset: '', message_template: DEFAULT_TEMPLATE });

    useEffect(() => {
        (async () => {
            const res = await api.get('v1/apps/default/');
            setAppId(res.data.app.id);
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        listarRegras();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    const listarRegras = async () => {
        const res = await api.get(`v1/apps/${appId}/collection-rules/`);
        setRules(res.data.rules || []);
    };

    const handleOpenDialog = () => {
        setForm({ name: '', days_offset: '', message_template: DEFAULT_TEMPLATE });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || form.days_offset === '') return;
        await api.post(`v1/apps/${appId}/collection-rules/`, form);
        setDialogOpen(false);
        listarRegras();
    };

    const handleToggleActive = async (rule) => {
        await api.patch(`v1/apps/${appId}/collection-rules/${rule.id}/`, { is_active: !rule.is_active });
        listarRegras();
    };

    const handleDelete = async (rule) => {
        if (!window.confirm(`Excluir a regra "${rule.name}"?`)) return;
        await api.delete(`v1/apps/${appId}/collection-rules/${rule.id}/`);
        setRules((prev) => prev.filter((r) => r.id !== rule.id));
    };

    const describeOffset = (daysOffset) => {
        if (daysOffset < 0) return `${Math.abs(daysOffset)} dia(s) antes do vencimento`;
        if (daysOffset > 0) return `${daysOffset} dia(s) depois do vencimento`;
        return 'No dia do vencimento';
    };

    return (
        <Box>
            <PageHeader
                icon={IconBellRinging}
                gradient="coral"
                title="Régua de Cobrança"
                subtitle="Lembretes automáticos por e-mail pra parcelas a receber, antes ou depois do vencimento"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenDialog}>
                        Nova Regra
                    </Button>
                }
            />

            <Card>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Nome</TableCell>
                                <TableCell>Quando dispara</TableCell>
                                <TableCell>Ativa</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rules.map((rule) => (
                                <TableRow key={rule.id}>
                                    <TableCell>{rule.name}</TableCell>
                                    <TableCell>
                                        <Chip label={describeOffset(rule.days_offset)} size="small" variant="outlined" />
                                    </TableCell>
                                    <TableCell>
                                        <Switch checked={rule.is_active} size="small" onChange={() => handleToggleActive(rule)} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => handleDelete(rule)}>
                                            <IconTrash size={16} />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {rules.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={4} align="center">
                                        Nenhuma regra de cobrança cadastrada
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Regra de Cobrança</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField label="Nome" fullWidth required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                        <TextField
                            label="Dias em relação ao vencimento"
                            type="number"
                            fullWidth
                            required
                            helperText="Negativo = antes do vencimento (lembrete preventivo). Positivo = depois (cobrança)."
                            value={form.days_offset}
                            onChange={(e) => setForm({ ...form, days_offset: e.target.value })}
                        />
                        <TextField
                            label="Mensagem"
                            fullWidth
                            multiline
                            minRows={3}
                            helperText="Variáveis disponíveis: {cliente}, {descricao}, {parcela}, {saldo}, {vencimento}"
                            value={form.message_template}
                            onChange={(e) => setForm({ ...form, message_template: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave}>
                        Criar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default CollectionRules;
